/**
 * Start and stop impersonating (ADR impersonation). These talk to the two Better Auth
 * endpoints in src/adapters/auth/impersonation.ts, which swap the session
 * cookie — so afterwards everything cached belongs to the wrong person and is
 * thrown away.
 */
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useCallback, useState } from 'react'
import { toast } from 'sonner'

async function post(path: string, body?: unknown): Promise<{ ok: boolean; message?: string }> {
  const response = await fetch(`/api/auth/impersonation/${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  })
  if (response.ok) return { ok: true }
  const data = (await response.json().catch(() => null)) as { message?: string } | null
  return { ok: false, message: data?.message ?? 'That did not work. Try again.' }
}

/** Ask the server to stop — also used when the hour runs out. */
export const stopImpersonatingRequest = () => post('stop')

export function useImpersonation() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [busy, setBusy] = useState(false)

  const start = useCallback(
    async (person: { userId: string; name: string }) => {
      setBusy(true)
      const result = await post('start', { userId: person.userId })
      setBusy(false)
      if (!result.ok) {
        toast.error(result.message)
        return
      }
      queryClient.clear()
      await navigate({ to: '/' })
    },
    [queryClient, navigate],
  )

  const stop = useCallback(async () => {
    setBusy(true)
    const result = await stopImpersonatingRequest()
    setBusy(false)
    if (!result.ok) {
      toast.error(result.message)
      if (result.message?.includes('Sign in')) await navigate({ to: '/sign-in' })
      return
    }
    queryClient.clear()
    await navigate({ to: '/settings/users' })
  }, [queryClient, navigate])

  return { start, stop, busy }
}
